import React from "react";
import { useBluelagoon } from "@bluelagoon";
import { useNavigate } from "react-router-dom";
import { Button } from "@shadcn-in-nx/ui/button";
import { Plus, Database, Table } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@shadcn-in-nx/ui/dialog";
import { Input } from "@shadcn-in-nx/ui/input";
import { Label } from "@shadcn-in-nx/ui/label";
import { Textarea } from "@shadcn-in-nx/ui/textarea";
import { Switch } from "@shadcn-in-nx/ui/switch";

function CreateDatabaseOrTableModal({
    onSuccess,
    triggerButton,
    className,
    mode = "database", // "database" or "table"
    databaseId = null, // Required when mode is "table"
    databaseName = "", // Optional: display name when creating table
    open, // Controlled mode: external open state
    onOpenChange, // Controlled mode: external open state handler
}) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [internalOpen, setInternalOpen] = React.useState(false);
    const [newDatabase, setNewDatabase] = React.useState({
        name: "",
        description: "",
    });
    const [createTable, setCreateTable] = React.useState(true);
    const [newTable, setNewTable] = React.useState({
        name: "",
        aiDescription: "",
    });
    const [isCreating, setIsCreating] = React.useState(false);
    const [error, setError] = React.useState("");

    // Use controlled mode if open/onOpenChange props are provided, otherwise use internal state
    const isControlled = open !== undefined && onOpenChange !== undefined;
    const isOpen = isControlled ? open : internalOpen;
    const setIsOpen = isControlled ? onOpenChange : setInternalOpen;

    const { call: createDatabaseCall } = useBluelagoon({
        method: "createDatabase",
    });

    const { call: createTableCall } = useBluelagoon({
        method: "createTable",
    });

    const handleCreateDatabase = async () => {
        if (!newDatabase.name.trim()) return;

        setIsCreating(true);
        setError(""); // Clear any previous errors

        try {
            // Create the database first
            const dbResponse = await createDatabaseCall({
                name: newDatabase.name.replace(/\s/g, ""),
                description: newDatabase.description.trim(),
            });

            // Check if the response indicates an error
            if (dbResponse?.success === false) {
                throw new Error(
                    dbResponse.data?.error ||
                        dbResponse.error ||
                        "Failed to create database"
                );
            }

            let tableId = null;
            // If table creation is enabled and we have a table name, create the table
            if (createTable && newTable.name.trim() && dbResponse?.data?.id) {
                try {
                    const tableResponse = await createTableCall({
                        databaseId: dbResponse.data.id,
                        name: newTable.name.replace(/\s/g, ""),
                        aiDescription: newTable.aiDescription.trim(),
                    });
                    console.log(tableResponse);

                    // Capture the table ID if creation was successful
                    if (tableResponse?.data?.id) {
                        tableId = tableResponse.data.id;
                    }
                } catch (tableError) {
                    console.error("Failed to create table:", tableError);
                    // Continue even if table creation fails
                }
            }

            // Reset form and close modal
            setNewDatabase({ name: "", description: "" });
            setNewTable({ name: "", aiDescription: "" });
            setIsOpen(false);

            // Call the onSuccess callback if provided
            if (onSuccess) {
                onSuccess();
            }

            // Navigate to the newly created table if it was created successfully
            if (tableId && dbResponse?.data?.id) {
                navigate(`/databases/${dbResponse.data.id}/tables/${tableId}`);
            }
        } catch (error) {
            console.error("Failed to create database:", error);
            setError(
                error.data?.error ||
                    error.error ||
                    error.message ||
                    error ||
                    "Failed to create database"
            );
        } finally {
            setIsCreating(false);
        }
    };

    const handleCreateTable = async () => {
        if (!newTable.name.trim() || !databaseId) return;

        setIsCreating(true);
        setError(""); // Clear any previous errors

        try {
            const tableResponse = await createTableCall({
                databaseId: databaseId,
                name: newTable.name.replace(/\s/g, ""),
                aiDescription: newTable.aiDescription.trim(),
            });

            // Check if the response indicates an error
            if (tableResponse?.success === false) {
                throw new Error(
                    tableResponse.data?.error ||
                        tableResponse.error ||
                        "Failed to create table"
                );
            }

            // Reset form and close modal
            setNewTable({ name: "", aiDescription: "" });
            setIsOpen(false);

            // Call the onSuccess callback if provided
            if (onSuccess) {
                onSuccess();
            }

            // Navigate to the newly created table if it was created successfully
            if (tableResponse?.data?.id) {
                navigate(
                    `/databases/${databaseId}/tables/${tableResponse.data.id}`
                );
            }
        } catch (error) {
            console.error("Failed to create table:", error);
            setError(
                error.data?.error ||
                    error.error ||
                    error.message ||
                    error ||
                    "Failed to create table"
            );
        } finally {
            setIsCreating(false);
        }
    };

    const handleSubmit = () => {
        if (mode === "table") {
            handleCreateTable();
        } else {
            handleCreateDatabase();
        }
    };

    const handleModalOpenChange = (open) => {
        setIsOpen(open);
        if (!open) {
            setNewDatabase({ name: "", description: "" });
            setNewTable({ name: "", aiDescription: "" });
            setError(""); // Clear errors when modal closes
        }
    };

    const getDefaultTrigger = () => {
        if (mode === "table") {
            return (
                <Button variant="outline" size="sm" className={className}>
                    <Plus className="w-4 h-4 mr-2" />
                    {t("createDatabaseModal.createTable")}
                </Button>
            );
        }
        return (
            <Button className={className}>
                <Plus className="w-4 h-4 mr-2" />
                {t("createDatabaseModal.createDatabase")}
            </Button>
        );
    };

    const getModalTitle = () => {
        if (mode === "table") {
            return databaseName
                ? t("createDatabaseModal.addTableToDatabase", { databaseName })
                : t("createDatabaseModal.addTableToDatabaseDefault");
        }
        return t("createDatabaseModal.createNewDatabase");
    };

    const getModalDescription = () => {
        if (mode === "table") {
            return t("createDatabaseModal.addTableDescription");
        }
        return t("createDatabaseModal.createDatabaseDescription");
    };

    const getSubmitButtonText = () => {
        if (mode === "table") {
            return isCreating
                ? t("createDatabaseModal.creatingTable")
                : t("createDatabaseModal.createTable");
        }
        return isCreating
            ? t("createDatabaseModal.creatingDatabase")
            : t("createDatabaseModal.createDatabase");
    };

    const isFormValid = () => {
        if (mode === "table") {
            return newTable.name.trim() && !isCreating;
        }
        return newDatabase.name.trim() && !isCreating;
    };

    // If controlled mode and no trigger button, render just the dialog content
    if (isControlled && !triggerButton) {
        return (
            <Dialog open={isOpen} onOpenChange={handleModalOpenChange}>
                <DialogContent className="sm:max-w-[450px] max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{getModalTitle()}</DialogTitle>
                        <DialogDescription>
                            {getModalDescription()}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        {/* Database Name - Only show when creating database */}
                        {mode === "database" && (
                            <div>
                                <Label
                                    htmlFor="name"
                                    className="text-sm font-medium text-gray-700 mb-2 block"
                                >
                                    {t("createDatabaseModal.databaseName")}
                                </Label>
                                <Input
                                    id="name"
                                    value={newDatabase.name}
                                    onChange={(e) =>
                                        setNewDatabase((prev) => ({
                                            ...prev,
                                            name: e.target.value,
                                        }))
                                    }
                                    placeholder={t(
                                        "createDatabaseModal.databaseNamePlaceholder"
                                    )}
                                    className="w-full"
                                />
                            </div>
                        )}

                        {/* Database Description - Only show when creating database */}
                        {mode === "database" && (
                            <div>
                                <Label
                                    htmlFor="description"
                                    className="text-sm font-medium text-gray-700 mb-2 block"
                                >
                                    {t(
                                        "createDatabaseModal.databaseDescription"
                                    )}
                                </Label>
                                <Textarea
                                    id="description"
                                    value={newDatabase.description}
                                    onChange={(e) =>
                                        setNewDatabase((prev) => ({
                                            ...prev,
                                            description: e.target.value,
                                        }))
                                    }
                                    placeholder={t(
                                        "createDatabaseModal.databaseDescriptionPlaceholder"
                                    )}
                                    className="w-full"
                                    rows={2}
                                />
                            </div>
                        )}

                        {/* Table Name */}
                        <div>
                            <Label
                                htmlFor="tableName"
                                className="text-sm font-medium text-gray-700 mb-2 block"
                            >
                                {t("createDatabaseModal.tableName")}
                            </Label>
                            <Input
                                id="tableName"
                                value={newTable.name}
                                onChange={(e) =>
                                    setNewTable((prev) => ({
                                        ...prev,
                                        name: e.target.value,
                                    }))
                                }
                                placeholder={t(
                                    "createDatabaseModal.tableNamePlaceholder"
                                )}
                                className="w-full"
                            />
                        </div>

                        {/* Table AI Description */}
                        <div>
                            <Label
                                htmlFor="tableDescription"
                                className="text-sm font-medium text-gray-700 mb-2 block"
                            >
                                {t("createDatabaseModal.tableDescription")}
                            </Label>
                            <Textarea
                                id="tableDescription"
                                value={newTable.aiDescription}
                                onChange={(e) =>
                                    setNewTable((prev) => ({
                                        ...prev,
                                        aiDescription: e.target.value,
                                    }))
                                }
                                placeholder={t(
                                    "createDatabaseModal.tableDescriptionPlaceholder"
                                )}
                                className="w-full"
                                rows={2}
                            />
                        </div>

                        {/* Error Display */}
                        {error && (
                            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                                <div className="flex items-center gap-2 text-red-800">
                                    <svg
                                        className="w-4 h-4"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                    <span className="text-sm font-medium">
                                        {t("createDatabaseModal.error")}
                                    </span>
                                </div>
                                <p className="text-red-700 text-sm mt-1">
                                    {error}
                                </p>
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setIsOpen(false)}
                        >
                            {t("createDatabaseModal.cancel")}
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            disabled={!isFormValid()}
                        >
                            {getSubmitButtonText()}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        );
    }

    // Default behavior with trigger button (uncontrolled mode)
    return (
        <Dialog open={isOpen} onOpenChange={handleModalOpenChange}>
            <DialogTrigger asChild>
                {triggerButton || getDefaultTrigger()}
            </DialogTrigger>
            <DialogContent className="sm:max-w-[450px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{getModalTitle()}</DialogTitle>
                    <DialogDescription>
                        {getModalDescription()}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {/* Database Name - Only show when creating database */}
                    {mode === "database" && (
                        <div>
                            <Label
                                htmlFor="name"
                                className="text-sm font-medium text-gray-700 mb-2 block"
                            >
                                {t("createDatabaseModal.databaseName")}
                            </Label>
                            <Input
                                id="name"
                                value={newDatabase.name}
                                onChange={(e) =>
                                    setNewDatabase((prev) => ({
                                        ...prev,
                                        name: e.target.value,
                                    }))
                                }
                                placeholder={t(
                                    "createDatabaseModal.databaseNamePlaceholder"
                                )}
                                className="w-full"
                            />
                        </div>
                    )}

                    {/* Database Description - Only show when creating database */}
                    {mode === "database" && (
                        <div>
                            <Label
                                htmlFor="description"
                                className="text-sm font-medium text-gray-700 mb-2 block"
                            >
                                {t("createDatabaseModal.databaseDescription")}
                            </Label>
                            <Textarea
                                id="description"
                                value={newDatabase.description}
                                onChange={(e) =>
                                    setNewDatabase((prev) => ({
                                        ...prev,
                                        description: e.target.value,
                                    }))
                                }
                                placeholder={t(
                                    "createDatabaseModal.databaseDescriptionPlaceholder"
                                )}
                                className="w-full"
                                rows={2}
                            />
                        </div>
                    )}

                    {/* Table Creation Toggle - Only show when creating database */}
                    {mode === "database" && (
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-2">
                                <Table className="w-4 h-4 text-gray-600" />
                                <span className="text-sm text-gray-700">
                                    {t("createDatabaseModal.includeTable")}
                                </span>
                            </div>
                            <Switch
                                checked={createTable}
                                onCheckedChange={setCreateTable}
                            />
                        </div>
                    )}

                    {/* Table Name */}
                    <div>
                        <Label
                            htmlFor="tableName"
                            className="text-sm font-medium text-gray-700 mb-2 block"
                        >
                            {t("createDatabaseModal.tableName")}
                        </Label>
                        <Input
                            id="tableName"
                            value={newTable.name}
                            onChange={(e) =>
                                setNewTable((prev) => ({
                                    ...prev,
                                    name: e.target.value,
                                }))
                            }
                            placeholder={t(
                                "createDatabaseModal.tableNamePlaceholder"
                            )}
                            className="w-full"
                        />
                    </div>

                    {/* Table AI Description */}
                    <div>
                        <Label
                            htmlFor="tableDescription"
                            className="text-sm font-medium text-gray-700 mb-2 block"
                        >
                            {t("createDatabaseModal.tableDescription")}
                        </Label>
                        <Textarea
                            id="tableDescription"
                            value={newTable.aiDescription}
                            onChange={(e) =>
                                setNewTable((prev) => ({
                                    ...prev,
                                    aiDescription: e.target.value,
                                }))
                            }
                            placeholder={t(
                                "createDatabaseModal.tableDescriptionPlaceholder"
                            )}
                            className="w-full"
                            rows={2}
                        />
                    </div>

                    {/* Error Display */}
                    {error && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                            <div className="flex items-center gap-2 text-red-800">
                                <svg
                                    className="w-4 h-4"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                                <span className="text-sm font-medium">
                                    {t("createDatabaseModal.error")}
                                </span>
                            </div>
                            <p className="text-red-700 text-sm mt-1">{error}</p>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsOpen(false)}
                    >
                        {t("createDatabaseModal.cancel")}
                    </Button>
                    <Button onClick={handleSubmit} disabled={!isFormValid()}>
                        {getSubmitButtonText()}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default CreateDatabaseOrTableModal;
